// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { translate } from "@/components/IntlProviderWrapper";
import Authorization from "@/components/Authorization";
import { getVisibleRoles, addUserRolesMap, getSelfDeactivationWarning } from "./lib/userRoles";
import type { UserWithRolesMap } from "./lib/userRoles";
import {
    fetchLastActiveForUsers,
    getFilteredUsers,
    getUuidsToFetch,
    markBatchError,
    markUuidsLoading,
    mergeLastActiveResponse,
} from "./lib/lastActive";
import type { LastActiveState } from "./lib/lastActive";

// backend
import * as Auth from "@/lib/Auth";
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import { USER_TAGS, USER_TAGS_ACCESS } from "@/lib/CommonTags.generated";
import { stringCompare, createCsvAndDownload, niceDateTime } from "@/lib/Util";

// import subcomponents
import ErrorComponent from "@/components/ErrorComponent";
import { Link } from "react-router-dom";
import Toggle from "react-toggle";
import { Button } from "react-bootstrap";

// style
import "./Users.scss";

// defining types
type Props = {
    match: match<{}>;
    history: History;
    location: Location;
};

type State = {
    users: UserWithRolesMap[];
    title_filter: string;
    only_active: boolean;
    error: string;
    warning: string;
    last_active_by_uuid: Record<string, LastActiveState>;
};

/**
 * Displaying user list.
 */
class UsersComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const state: State = {
            users: [],
            title_filter: "",
            only_active: false,
            error: "",
            warning: "",
            last_active_by_uuid: {},
        };
        this.state = state;
    }

    componentDidMount() {
        this.reloadData();
    }

    componentDidUpdate(_prev_props: Props, prev_state: State) {
        if (
            prev_state.users !== this.state.users ||
            prev_state.title_filter !== this.state.title_filter ||
            prev_state.only_active !== this.state.only_active
        ) {
            this.fetchLastActiveForVisibleUsers();
        }
    }

    /**
     * Actual loading data from backend.
     */
    async reloadData() {
        try {
            const res = await getBackend2().users.getUsers({});
            const users: UserWithRolesMap[] = res.users.map(addUserRolesMap);
            users.sort((a, b) => stringCompare(a.username, b.username));
            this.setState({ users: users, last_active_by_uuid: {} });
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
    }

    getFilteredUsers(): UserWithRolesMap[] {
        return getFilteredUsers(this.state.users, this.state.title_filter, this.state.only_active);
    }

    async fetchLastActiveForVisibleUsers() {
        const visible_uuids = this.getFilteredUsers().map(user => user.uuid);
        const to_fetch = getUuidsToFetch(visible_uuids, this.state.last_active_by_uuid);

        await fetchLastActiveForUsers(
            to_fetch,
            batch => getBackend2().users.getUsersLastActive({ user_uuids: batch }),
            {
                on_loading: uuids => {
                    this.setState(prev_state => ({
                        last_active_by_uuid: markUuidsLoading(prev_state.last_active_by_uuid, uuids),
                    }));
                },
                on_batch_success: users => {
                    this.setState(prev_state => ({
                        last_active_by_uuid: mergeLastActiveResponse(prev_state.last_active_by_uuid, users),
                    }));
                },
                on_batch_error: batch => {
                    this.setState(prev_state => ({
                        last_active_by_uuid: markBatchError(prev_state.last_active_by_uuid, batch),
                    }));
                },
            }
        );
    }

    renderLastActiveCell(user_uuid: string) {
        const state = this.state.last_active_by_uuid[user_uuid];
        if (state === undefined || state === "loading") {
            return <i className="fas fa-spinner fa-spin" aria-hidden="true" />;
        }
        if (state === "error") {
            return "—";
        }
        if (state === null) {
            return <FormattedMessage id="Users.last_active_never" defaultMessage="Never" />;
        }
        return niceDateTime(new Date(state));
    }

    updateKioskValue = (user: UserWithRolesMap, value: boolean) => {
        this.setState(
            prev_state => {
                const user_prev = prev_state.users.find(u => u.uuid === user.uuid);
                if (user_prev !== undefined) {
                    user_prev.roles_map.set(USER_TAGS.kiosk, !value);
                }
                return prev_state;
            },
            async () => {
                await getBackend2().users.setUserTag({
                    id: user.uuid,
                    name: USER_TAGS.kiosk,
                    value: value === true ? "false" : "true",
                });
            }
        );
    };

    /** Handle role toggling (checkbox clicks) */
    handleRoleToggle = async (user: UserWithRolesMap, role_id: string) => {
        try {
            const value = user.roles_map.get(role_id);
            const new_value = !value;

            if (new_value === false) {
                const warning = getSelfDeactivationWarning(role_id);
                if (warning != null) {
                    if (user.username === Auth.getLoggedinUser().username) {
                        this.setState({ warning });
                        return;
                    }
                }
            }

            // Special case for kiosk role
            if (role_id === USER_TAGS.kiosk) {
                this.updateKioskValue(user, Boolean(value));
            } else {
                if (new_value === false) {
                    await getBackend2().users.removeRoleForUser({ id: user.uuid, role: role_id });
                } else {
                    await getBackend2().users.addRoleForUser({ id: user.uuid, role: role_id });
                }
                this.reloadData();
            }
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (name === "only_active") {
            value = (target as HTMLInputElement).checked;
        }

        // update new datasource placeholder
        this.setState(prev_state => ({
            ...prev_state,
            [name]: value,
        }));
    };

    userSetActive = async (uuid: string, active: boolean) => {
        try {
            await getBackend2().users.userSetActive({ id: uuid, active });
            this.reloadData();
            if (this.state.error) this.setState({ error: "" });
        } catch (err) {
            this.setState({
                error: translate("common.user_active_error", "Could not activate/deactivate user"),
            });
        }
    };

    exportUsers = async () => {
        try {
            this.setState({ error: "" });
            const { csv } = await getBackend2().users.getUsersExport({});
            createCsvAndDownload(csv, "users.csv");
        } catch (err: unknown) {
            this.setState({
                error: (err as Error).message,
            });
        }
    };

    showUserInfo = (uuid: string) => {
        this.props.history.push("/admin/user_info/" + uuid);
    };

    /**
     * Rendering JSX for current component.
     */
    render() {
        // check if user is in kiosk mode
        const is_kiosk = Auth.getUserTag("kiosk") === "true";
        // is the user in admin role
        const is_admin = !is_kiosk && Auth.isInRole(Auth.ROLE_ADMIN);

        const org_code = Auth.getOrgData().code;

        // do we show the table?
        const showTable = this.state.users.length > 0;

        const filtered_users = this.getFilteredUsers();

        const visible_user_roles = getVisibleRoles({ is_admin, org_code });

        return (
            <article id="admin-users" className="lines_article">
                <section id="statistics" className="data_sources">
                    <div className="filter_bar">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-lg-12">
                                    <ul className="list-inline">
                                        <li className="list-inline-item">
                                            <h2 className="title-big">
                                                <FormattedMessage id="common.users" defaultMessage="Users" />
                                            </h2>
                                        </li>
                                        <li className="list-inline-item">
                                            <div className="container-fluid">
                                                <div className="form-group">
                                                    <FormattedMessage
                                                        id="Users.filter_tooptip"
                                                        defaultMessage="Filter by title or user"
                                                    >
                                                        {text => (
                                                            <input
                                                                type="text"
                                                                className="form-control search_bar"
                                                                placeholder={String(text)}
                                                                name="title_filter"
                                                                value={this.state.title_filter}
                                                                onChange={this.handleInputChange}
                                                            />
                                                        )}
                                                    </FormattedMessage>
                                                </div>
                                                <div className="form-group">
                                                    <label>
                                                        <FormattedMessage
                                                            id="common.only_active"
                                                            defaultMessage="Only active"
                                                        />
                                                    </label>
                                                    <Toggle
                                                        name="only_active"
                                                        defaultChecked={this.state.only_active}
                                                        onChange={this.handleInputChange}
                                                        className="vertical-align-middle"
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <Link to="/admin/user_new" className="btn_toolbar btn_add_data">
                                                        <FormattedMessage id="common.add" defaultMessage="Add" />
                                                    </Link>
                                                </div>
                                                <div className="form-group">
                                                    <Button
                                                        className="btn_toolbar btn_export"
                                                        onClick={() => this.exportUsers()}
                                                    >
                                                        <FormattedMessage
                                                            id="common.export"
                                                            defaultMessage="Export data"
                                                        />
                                                    </Button>
                                                </div>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="container-fluid space15">
                        {showTable && (
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="white_box charts">
                                        <ErrorComponent msg={this.state.error} type="error" />
                                        <ErrorComponent msg={this.state.warning} type="warning" />
                                        <div className="table_content users__table">
                                            <table
                                                id="datatable-responsive"
                                                className="table bottom_table stripe_cols_table"
                                                cellSpacing="0"
                                                width="100%"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th>
                                                            <FormattedMessage
                                                                id="common.username"
                                                                defaultMessage="Username"
                                                            />
                                                        </th>
                                                        <th>
                                                            <FormattedMessage
                                                                id="common.title"
                                                                defaultMessage="Title"
                                                            />
                                                        </th>
                                                        <th>
                                                            <FormattedMessage
                                                                id="common.status"
                                                                defaultMessage="Status"
                                                            />
                                                        </th>
                                                        <th>
                                                            <FormattedMessage
                                                                id="Users.last_active"
                                                                defaultMessage="Last active"
                                                            />
                                                        </th>
                                                        {visible_user_roles.map(role => (
                                                            <th key={`header-user-role-${role.id}`}>
                                                                <div className="vertical-text">
                                                                    <FormattedMessage id={role.label_id} />
                                                                </div>
                                                            </th>
                                                        ))}
                                                        <th>&nbsp;</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered_users.map((user, index) => {
                                                        return (
                                                            <tr key={index}>
                                                                <td title={user.uuid}>
                                                                    {user.username}
                                                                    <br />
                                                                    <span className="text-muted">{user.uuid}</span>
                                                                </td>
                                                                <td>{user.title}</td>
                                                                <td>
                                                                    {user.active ? (
                                                                        <span className="badge badge-success">
                                                                            <FormattedMessage
                                                                                id="common.active"
                                                                                defaultMessage="Active"
                                                                            />
                                                                        </span>
                                                                    ) : (
                                                                        <span className="badge badge-secondary">
                                                                            <FormattedMessage
                                                                                id="common.inactive"
                                                                                defaultMessage="Inactive"
                                                                            />
                                                                        </span>
                                                                    )}
                                                                    <br />
                                                                    {user.must_reset && (
                                                                        <span className="badge badge-warning">
                                                                            <FormattedMessage
                                                                                id="common.password_reset"
                                                                                defaultMessage="Password reset"
                                                                            />
                                                                        </span>
                                                                    )}
                                                                    {user.must_activate_twofa && (
                                                                        <span className="badge badge-warning">
                                                                            <FormattedMessage
                                                                                id={
                                                                                    "common.twofa_must_activate" +
                                                                                    niceDateTime(
                                                                                        new Date(
                                                                                            user.must_activate_twofa_date
                                                                                        )
                                                                                    )
                                                                                }
                                                                                defaultMessage={
                                                                                    "Must activate 2FA until: " +
                                                                                    niceDateTime(
                                                                                        new Date(
                                                                                            user.must_activate_twofa_date
                                                                                        )
                                                                                    )
                                                                                }
                                                                            />
                                                                        </span>
                                                                    )}
                                                                    {USER_TAGS_ACCESS.twofa(user.tags) && (
                                                                        <span className="badge badge-success">
                                                                            <FormattedMessage
                                                                                id="2FA"
                                                                                defaultMessage="2FA"
                                                                            />
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td>{this.renderLastActiveCell(user.uuid)}</td>
                                                                {visible_user_roles.map(role => (
                                                                    <td key={`toggle-user-role-${role.id}`}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={user.roles_map.get(role.id)}
                                                                            onChange={() => {
                                                                                this.handleRoleToggle(user, role.id);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                ))}
                                                                <td>
                                                                    <div style={{ minWidth: "100px" }}>
                                                                        <Authorization.button
                                                                            permission={
                                                                                Auth.PERMISSION_NAMES.ElevatedPriviliges
                                                                            }
                                                                            className="far fa-edit btn btn-short btn-outline-primary"
                                                                            style={{
                                                                                marginLeft: "5px",
                                                                                cursor: "pointer",
                                                                            }}
                                                                            onClick={() => {
                                                                                this.showUserInfo(user.uuid);
                                                                            }}
                                                                            title={"Edit user"}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </article>
        );
    }
}

export default UsersComponent;
